import { useEffect, useCallback, useRef, ReactNode } from "react";
import { Button } from "./button";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CarouselItem {
  id: string | number;
  content: ReactNode;
}

interface CarouselProps {
  items: CarouselItem[];
  selectedIndex: number; // pass -1 for no selection
  onItemSelect: (index: number) => void;
  className?: string;
  itemClassName?: string;
  selectedItemClassName?: string;
  disabled?: boolean;
}

export const Carousel = ({
  items,
  selectedIndex,
  onItemSelect,
  className = "",
  itemClassName = "",
  selectedItemClassName = "",
  disabled = false,
}: CarouselProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const selectedChipRef = useRef<HTMLButtonElement>(null);

  const goPrev = useCallback(() => {
    if (disabled) return;
    const nextIndex = selectedIndex <= 0 ? items.length - 1 : selectedIndex - 1;
    onItemSelect(nextIndex);
  }, [selectedIndex, items.length, onItemSelect, disabled]);

  const goNext = useCallback(() => {
    if (disabled) return;
    const nextIndex = selectedIndex >= items.length - 1 ? 0 : selectedIndex + 1;
    onItemSelect(nextIndex);
  }, [selectedIndex, items.length, onItemSelect, disabled]);

  // Auto-scroll selected chip into view
  const scrollToSelected = useCallback(() => {
    if (selectedChipRef.current && carouselRef.current) {
      // Use requestAnimationFrame to ensure DOM is rendered
      requestAnimationFrame(() => {
        selectedChipRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      });
    }
  }, []);

  // Handle initial positioning when component mounts or data changes
  useEffect(() => {
    if (items && items.length > 0) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        scrollToSelected();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [items, scrollToSelected]);

  // Handle position changes during interaction
  useEffect(() => {
    scrollToSelected();
  }, [selectedIndex, scrollToSelected]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 flex-1 min-w-0 ${className}`}>
      <Button
        variant="outline"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={goPrev}
        disabled={disabled}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <div className="flex-1 min-w-0 relative">
        <div
          ref={carouselRef}
          className="flex items-center gap-1 overflow-x-auto px-1 carousel-container"
          style={{
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          {items.map((item, idx) => (
            <button
              key={item.id}
              ref={idx === selectedIndex ? selectedChipRef : null}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-all duration-50 whitespace-nowrap shrink-0 ${
                selectedIndex >= 0 && idx === selectedIndex
                  ? `bg-stone-950 text-white border shadow-sm scale-105 ${selectedItemClassName}`
                  : `bg-white text-stone-700 border-stone-200 hover:bg-stone-50 hover:border-stone-300 ${itemClassName}`
              }`}
              onClick={() => onItemSelect(idx)}
              disabled={disabled}
            >
              {item.content}
            </button>
          ))}
        </div>
      </div>
      <Button
        variant="outline"
        size="icon"
        className="h-7 w-7 shrink-0"
        onClick={goNext}
        disabled={disabled}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};
